import type { Ref } from 'react';
import { useMemo } from 'react';
import { mergeRefs } from '@react-aria/utils';

/**
 * A React hook that merges up to four refs into a single ref callback.
 *
 * This hook is useful when you need to attach multiple refs to the same DOM element,
 * such as when combining a forwarded ref with an internal ref, or when using multiple
 * third-party libraries that each provide their own ref.
 *
 * @template T - The type of the element that the refs will be attached to
 * @param first - The first ref to merge
 * @param second - The second ref to merge
 * @param third - An optional third ref to merge
 * @param fourth - An optional fourth ref to merge
 * @returns A memoized ref callback that will call all provided refs when attached to an element
 *
 * @example
 * ```tsx
 * const MyComponent = forwardRef<HTMLDivElement, MyProps>((props, forwardedRef) => {
 *   const internalRef = useRef<HTMLDivElement>(null);
 *   const mergedRef = useMergeRefs(forwardedRef, internalRef);
 *
 *   return <div ref={mergedRef}>Content</div>;
 * });
 * ```
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const ref1 = useRef<HTMLButtonElement>(null);
 *   const ref2 = useRef<HTMLButtonElement>(null);
 *   const mergedRef = useMergeRefs(ref1, ref2, undefined); // undefined refs are filtered out
 *
 *   return <button ref={mergedRef}>Click me</button>;
 * }
 * ```
 */
export function useMergeRefs<T>(
  first: Ref<T> | undefined,
  second: Ref<T> | undefined,
  third?: Ref<T>,
  fourth?: Ref<T>,
) {
  return useMemo(
    () => mergeRefs(...[first, second, third, fourth].filter((ref) => ref !== undefined && ref !== null)),
    [first, second, third, fourth],
  );
}
